/* eslint @typescript-eslint/strict-boolean-expressions: 0 */
import {
  compact,
  concat,
  endsWith,
  filter,
  find,
  findIndex,
  findLast,
  flatten,
  groupBy,
  head,
  includes,
  isEmpty,
  isNil,
  join,
  keys,
  last,
  map,
  max,
  maxBy,
  range,
  reduce,
  replace,
  size,
  slice,
  split
} from 'lodash/fp'
import { Chart, getElementAtEvent } from 'react-chartjs-2'
import {
  BarController,
  BarElement,
  Chart as ChartJS,
  CategoryScale,
  LineController,
  Filler,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  type ChartConfiguration,
  Legend
} from 'chart.js'
import annotationPlugin from 'chartjs-plugin-annotation'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import {
  ElectricityChartDailyKind,
  ElectricityChartMonthlyKind,
  ElectricityChartYearlyKind,
  type Frame,
  type ElectricityDaily,
  type ElectricityMonthly,
  type ElectricityYearly,
  type ElectricityDailyData,
  type ElectricityYearlyData,
  type ElectricityMonthlyData,
  type Comment
} from '../../../../cdk/bapp/lib/lambda-api-v2/src/types'
import React, { type ReactElement } from 'react'
import log from 'loglevel'
import {
  type Identifier,
  Loading,
  type RaRecord,
  TextInput,
  useGetIdentity,
  useRecordContext,
  useTranslate
} from 'react-admin'
import {
  useGetComments,
  useGetElectricityDailies,
  useGetElectricityDailyBySpin,
  useGetElectricityDailyCompareToBySpin,
  useGetElectricityDailyTargets,
  useGetElectricityDemand,
  useGetElectricityDemandDaily,
  useGetElectricityDemandMonthly,
  useGetElectricityMonthlies,
  useGetElectricityMonthlyBySpin,
  useGetElectricityMonthlyCompareToBySpin,
  useGetElectricityMonthlyTargets,
  useGetElectricityYearlyBySpin,
  useGetElectricityYearlyCompareToBySpin
} from 'components/DataProvider'
import {
  createDailyChart,
  createDemandDailyChart,
  createDemandMonthlyChart,
  createDemandTrendChart,
  createDemandYearlyChart,
  createMonthlyChart,
  createMonthlyChartByDays,
  createYearlyChart
} from './chartjs'
import { useNotify, type ScrollProps } from 'components/Util'
import { type ConfigProps } from 'components/Dashboard'
import {
  Box,
  Button,
  Grid,
  IconButton,
  Popover,
  Typography
} from '@mui/material'
import { flushSync } from 'react-dom'
import { createRoot } from 'react-dom/client'
import {
  addDays,
  eachDayOfInterval,
  eachMinuteOfIntervalWithOptions,
  endOfMonth,
  format,
  getDate,
  getDay,
  getMinutes,
  getWeeksInMonthWithOptions,
  isBefore,
  parseISO,
  startOfMonth
} from 'date-fns/fp'
import { demandHints } from './demandHints'
import HelpCenterIcon from '@mui/icons-material/HelpCenter'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import BusinessIcon from '@mui/icons-material/Business'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import { json2xlsx } from '@mzc-watchover/json2xlsx'
import { type Workbook } from 'xlsx-populate'
import { useFormContext } from 'react-hook-form'
import {
  doAnalyzeDaily,
  doAnalyzeYearly,
  postComment
} from 'components/DataProvider/functions'
import { assign } from 'lodash'
import { useNavigate } from 'react-router-dom'

ChartJS.register(
  BarController,
  BarElement,
  CategoryScale,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  Filler,
  Title,
  Legend,
  annotationPlugin,
  ChartDataLabels
)

const AmountLabel: React.FC<{
  amountsId?: string
  amountsTotal?:
    | {
        amounts: number
        compareAmounts: number | undefined
        compareKind?: string | undefined
      }
    | null
    | undefined
  width: string
  action: string | null | undefined
  date?: string
  record?: RaRecord
  partner?: string
}> = ({ amountsId, amountsTotal, width, action, date, record, partner }) => {
  const translate = useTranslate()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [thumbUpColor, setThumbUpColor] = React.useState<string | null>('white')
  const [thumbDownColor, setThumbDownColor] = React.useState<string | null>(
    'white'
  )
  const [disabled, setDisabled] = React.useState(false)
  const { getValues, setValue } = useFormContext()
  const { data, isLoading } = useGetIdentity()
  const [root, setRoot] = React.useState<any>(null)
  const { data: comments, isLoading: isLoadingComments } = useGetComments(
    partner,
    format('yyyy-MM-dd')(Date.now())
  )
  const handleClick = isNil(date)
    ? null
    : (up: boolean) =>
        (event: React.MouseEvent<HTMLElement>): void => {
          if (up) {
            setThumbUpColor('#F1CDCE')
            setThumbDownColor('white')
          } else {
            setThumbDownColor('#BCCEFB')
            setThumbUpColor('white')
          }
          setAnchorEl(event.currentTarget)
        }
  const handleClose = (): void => {
    setAnchorEl(null)
    setValue('comment', null, { shouldDirty: true })
    setDisabled(true)
  }
  const handleSend = (): void => {
    if (isNil(data) || isNil(partner) || isNil(date) || isNil(record)) return
    const { comment } = getValues()
    const {
      fullName,
      cognitoUser: {
        signInUserSession: {
          idToken: {
            payload: { email }
          }
        }
      }
    } = data
    if (isNil(fullName)) return
    if (!isNil(action)) {
      postComment(
        partner,
        date,
        fullName,
        email,
        record,
        action,
        comment,
        !isNil(thumbUpColor)
      )
        .then(() => {
          setAnchorEl(null)
          setValue('comment', null, { shouldDirty: true })
        })
        .catch(log.error)
    }
  }
  const onChange = (event: any): void => {
    setDisabled(isEmpty(event.target.value))
  }
  React.useEffect(() => {
    if (isLoading || isLoadingComments) return
    if (isNil(record) || isNil(data)) return
    const { spin } = record
    const {
      fullName,
      cognitoUser: {
        signInUserSession: {
          idToken: {
            payload: { email }
          }
        }
      }
    } = data
    const commentItem = findLast(
      (item: Comment) =>
        !isNil(item.target) &&
        item.target.spin === spin &&
        item.name === fullName &&
        item.email === email
    )(comments)
    if (!isNil(commentItem)) {
      const { up, comment } = commentItem
      if (up) {
        setThumbUpColor('#F1CDCE')
        setThumbDownColor(null)
      } else {
        setThumbDownColor('#BCCEFB')
        setThumbUpColor(null)
      }
      setValue('comment', comment, { shouldDirty: true })
    }
  }, [isLoadingComments, comments])
  const { innerWidth } = window
  React.useEffect(() => {
    queueMicrotask(() => {
      if (!isNil(amountsId)) {
        const amountsContainer = document.getElementById(amountsId)
        if (!isNil(amountsContainer)) {
          let rootValue = root
          if (isNil(root)) {
            rootValue = createRoot(amountsContainer)
            setRoot(rootValue)
          }
          if (!isNil(amountsTotal)) {
            flushSync(() => {
              rootValue.render(
                createAmountsLabel(
                  amountsTotal,
                  width,
                  handleClick,
                  action,
                  thumbUpColor,
                  thumbDownColor
                )
              )
            })
          } else {
            flushSync(() => {
              rootValue.render(<></>)
            })
          }
        }
      }
    })
  }, [
    amountsId,
    amountsTotal,
    thumbUpColor,
    thumbDownColor,
    document,
    action,
    innerWidth,
    root
  ])
  if (isLoading || isLoadingComments) return null
  return (
    <Popover
      open={!isNil(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}>
      <Box sx={{ border: 1, p: 1, bgcolor: 'background.paper', width: '30em' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignContent: 'space-between'
          }}>
          <TextInput
            source="comment"
            label="resources.spots.daily.comment.label"
            multiline
            placeholder={translate('resources.spots.daily.comment.placeholder')}
            onChange={onChange}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" sx={{ mr: '1em' }} onClick={handleClose}>
              {translate('resources.spots.daily.comment.buttons.close')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={disabled}>
              {translate('resources.spots.daily.comment.buttons.send')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Popover>
  )
}
const DemandLabel: React.FC<{
  partner: string
  demandId?: string
  demand?:
    | {
        amount: number
        date: string
        latest: { amount: number | undefined; date: string | undefined }
      }
    | null
    | undefined
  latestLabel?: string
  latestFormat?: string
  maxHour?: number
  durations?: number[][]
}> = ({
  partner,
  demandId,
  demand,
  latestLabel,
  latestFormat,
  maxHour,
  durations
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [root, setRoot] = React.useState<any>(null)
  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = (): void => {
    setAnchorEl(null)
  }
  const hint = find(
    (item: { partner: string; element: () => ReactElement }) =>
      item.partner === partner || item.partner === '*'
  )(demandHints)
  const { innerWidth } = window
  React.useEffect(() => {
    queueMicrotask(() => {
      if (!isNil(demandId) && !isNil(latestLabel) && !isNil(latestFormat)) {
        const demandContainer = document.getElementById(demandId)
        if (!isNil(demandContainer)) {
          let rootValue = root
          if (isNil(rootValue)) {
            rootValue = createRoot(demandContainer)
            setRoot(rootValue)
          }
          flushSync(() => {
            rootValue.render(
              !isNil(demand) ? (
                createDemandLabel(
                  demand,
                  latestLabel,
                  latestFormat,
                  maxHour,
                  durations,
                  handleClick
                )
              ) : (
                <></>
              )
            )
          })
        }
        // const hintContainer = document.getElementById(`${demandId}Hint`)
        // if (!isNil(hintContainer)) {
        //   if (!isNil(hint)) {
        //     const { element } = hint
        //     const root = createRoot(hintContainer)
        //     flushSync(() => {
        //       root.render(!isNil(demand) ? element() : <></>)
        //     })
        //   }
        // }
      }
    })
  }, [
    demandId,
    demand,
    innerWidth,
    root,
    latestLabel,
    latestFormat,
    maxHour,
    durations
  ])
  return !isNil(hint) ? (
    <Popover
      open={!isNil(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left'
      }}>
      <Box sx={{ border: 1, p: 1, bgcolor: 'background.paper' }}>
        {hint.element()}
      </Box>
    </Popover>
  ) : (
    <></>
  )
}
export const SolarChart: React.FC<{
  chart: ChartConfiguration
  defaultHeight?: number
  id?: string
  onClickItem?: (index: number) => void
}> = ({ chart, id, defaultHeight, onClickItem }) => {
  const { type, options, data, plugins } = chart
  const height = defaultHeight ?? Math.floor(window.innerHeight * 0.48)
  const minimumHeight = chart.minimumHeight ?? 320
  const [chartHeight, setChartHeight] = React.useState<number>(
    height < minimumHeight ? minimumHeight : height
  )
  const [chartWidth, setChartWidth] = React.useState<number>(window.innerWidth)
  const onResize = (): void => {
    const { innerWidth, innerHeight } = window
    const height = Math.floor(innerHeight * 0.48)
    setChartHeight(defaultHeight ?? (height < 516 ? 516 : height))
    setChartWidth(innerWidth)
  }
  const resizeChart = (
    chart: any,
    size: { width: number; height: number }
  ): void => {
    chart.resize(size.width, chartHeight)
  }
  const chartRef = React.useRef()
  const onClickRaw = (event: React.MouseEvent<HTMLCanvasElement>): void => {
    if (isNil(chartRef.current)) return
    const data = getElementAtEvent(chartRef.current, event)
    if (isNil(data[0])) return
    // @ts-expect-error ts(2339)
    const { index } = data[0].element.$context
    if (!isNil(onClickItem)) onClickItem(index)
  }
  React.useEffect(() => {
    window.addEventListener('resize', onResize)
  })
  if (isNil(options)) return <></>
  return (
    <div>
      {/* <div id="legend-container" /> */}
      <div>
        <Chart
          type={type}
          updateMode="resize"
          width={`${chartWidth}px`}
          height={`${chartHeight}px`}
          options={{
            ...options,
            maintainAspectRatio: false,
            onResize: resizeChart
          }}
          plugins={!isNil(plugins) ? plugins : []}
          data={data}
          id={id ?? 'solar-chart'}
          onClick={onClickRaw}
          ref={chartRef}
        />
      </div>
    </div>
  )
}
const labelHeight = 10
const createAmountsLabel = (
  amountsTotal: {
    amounts: number
    compareAmounts: number | undefined
    compareKind?: string | undefined
  },
  width: string,
  onClick:
    | ((up: boolean) => (event: React.MouseEvent<HTMLElement>) => void)
    | null
    | undefined,
  action: string | null | undefined,
  thumbUpColor: string | null,
  thumbDownColor: string | null
): ReactElement => {
  const { amounts, compareAmounts, compareKind } = amountsTotal
  const diff =
    !isNil(compareAmounts) && compareAmounts !== 0
      ? amounts - compareAmounts
      : undefined
  const diffRatio =
    !isNil(diff) && !isNil(compareAmounts)
      ? Math.floor((diff / compareAmounts) * 1000) / 10
      : undefined
  const actionItems = groupBy<any>('kind')(
    JSON.parse(isNil(action) || isEmpty(action) ? '[]' : action)
  )
  const sx = {
    '--Grid-borderWidth': '2px',
    borderRight: 'var(--Grid-borderWidth) dashed',
    borderColor: '#f57800'
  }
  const reactions = !isNil(onClick) && (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        mt: window.innerWidth <= 900 ? '1em' : 0
        // mr: '1.5em'
      }}>
      <Button
        size={window.innerWidth < 1200 ? 'small' : 'large'}
        onClick={onClick(true)}
        sx={{ boxShadow: 1, backgroundColor: '#EAEAEA' }}>
        <ThumbUpIcon sx={!isNil(thumbUpColor) ? { color: thumbUpColor } : {}} />
      </Button>
      <Button
        size={window.innerWidth < 1200 ? 'small' : 'large'}
        onClick={onClick(false)}
        sx={{ boxShadow: 1, backgroundColor: '#EAEAEA' }}>
        <ThumbDownIcon sx={{ color: thumbDownColor }} />
      </Button>
    </Box>
  )
  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
        <Box
          sx={{
            border: 2.5,
            p: '1em',
            mt: width > '40%' ? '2em' : '2em',
            height: `${labelHeight}em`,
            width: '95%',
            borderRadius: '10px',
            borderStyle: 'dashed',
            borderColor: '#f57800',
            backgroundColor: 'white'
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
            <Box
              sx={{
                height: `${labelHeight - 2.5}em`,
                overflowY: 'auto'
              }}>
              {size(keys(actionItems)) === 2 ? (
                <Grid container spacing={1}>
                  {map((kind: string) => (
                    <Grid size={6} sx={kind === '0' ? sx : {}}>
                      {
                        // @ts-expect-error ts(2339)
                        map.convert({ cap: false })(
                          (item: Record<string, string>, index: number) =>
                            !isNil(item['label']) ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center'
                                }}>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {kind === '0' ? (
                                    <CalendarMonthIcon />
                                  ) : (
                                    <BusinessIcon />
                                  )}
                                  <Typography
                                    // variant={
                                    //   startsWith('【')(
                                    //     item[keys(item)[0] as string] as string
                                    //   )
                                    //     ? 'h6'
                                    //     : 'inherit'
                                    // }
                                    variant="h5"
                                    key={index}>
                                    {`${item['label']}`}
                                    <br />
                                  </Typography>
                                </Box>
                              </Box>
                            ) : !isNil(item['action']) ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start'
                                }}>
                                <br />
                                {
                                  // @ts-expect-error ts(2339)
                                  map.convert({ cap: false })(
                                    (line: string, index: number) => (
                                      <Typography
                                        variant="h5"
                                        // sx={{ textDecoration: 'underline' }}
                                        sx={{ fontWeight: 'bold' }}
                                        color="red"
                                        key={index}>
                                        {`➡︎${line}`}
                                        <br />
                                      </Typography>
                                    )
                                  )(split('\n')(item['action']))
                                }
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'flex-start'
                                }}>
                                <Typography
                                  // variant={
                                  //   startsWith('【')(
                                  //     item[keys(item)[0] as string] as string
                                  //   )
                                  //     ? 'h6'
                                  //     : 'inherit'
                                  // }
                                  variant="h5"
                                  color={
                                    size(actionItems[kind]) === 3
                                      ? 'red'
                                      : 'blue'
                                  }
                                  key={index}>
                                  {`${
                                    size(actionItems[kind]) === 3 ? '▲' : '▼'
                                  }${item['result'] as string}`}
                                  <br />
                                </Typography>
                              </Box>
                            )
                        )(actionItems[kind])
                      }
                    </Grid>
                  ))(keys(actionItems))}
                </Grid>
              ) : (
                // @ts-expect-error ts(2339)
                map.convert({ cap: false })(
                  (item: Record<string, string>, index: number) => (
                    <Typography
                      // variant={
                      //   startsWith('【')(
                      //     item[keys(item)[0] as string] as string
                      //   )
                      //     ? 'h6'
                      //     : 'inherit'
                      // }
                      variant="h5"
                      key={index}>
                      {item['result']}
                      <br />
                    </Typography>
                  )
                )(actionItems[keys(actionItems)[0] as string])
              )}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: window.innerWidth <= 900 ? 'column' : 'row',
            justifyContent: 'center',
            pt: '0.5em',
            pb: '0.5em',
            backgroundColor: 'background.paper',
            border: 1,
            borderColor: '#f57800',
            borderRadius: '10px',
            width,
            alignItems: 'center',
            top: `-${labelHeight + (width > '40%' ? 2 : 2)}em`,
            position: 'relative',
            // height: width > '40%' ? '5em' : '3em',
            height: '3em',
            mb: width > '40%' ? '-3em' : '-3em'
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'space-between',
              mr: '1em'
            }}>
            <Box
              sx={{
                display: 'flex',
                // flexDirection: window.innerWidth < 1200 ? 'column' : 'row',
                // alignItems: window.innerWidth < 1200 ? 'center' : 'flex-end',
                alignItems: 'center',
                ml: window.innerWidth < 900 ? '10%' : '25%'
                // '25%'
              }}>
              <Typography
                // variant={window.innerWidth < 1450 ? 'h5' : 'h4'}
                variant="h5">
                電力量{amounts.toLocaleString()}kWh
              </Typography>
              {!isNil(diff) && (
                <>
                  {!isNil(compareKind) && (
                    <Typography
                      variant={window.innerWidth < 1250 ? 'body1' : 'h6'}
                      sx={{ ml: '1em' }}>
                      {`${compareKind}比較`}
                    </Typography>
                  )}
                  <Typography
                    sx={{ color: 'red' }}
                    variant={window.innerWidth < 1250 ? 'body1' : 'h6'}>
                    {/* variant="h6"> */}【{diff > 0 ? '+' : '▲'}
                    {Math.floor(diff < 0 ? -diff : diff).toLocaleString()}kWh
                    {isNil(diffRatio)
                      ? ''
                      : `（${diffRatio < 0 ? '▲' : ''}${
                          diffRatio < 0 ? -diffRatio : diffRatio
                        }%）`}
                    】
                  </Typography>
                </>
              )}
            </Box>
            {reactions}
          </Box>
        </Box>
      </Box>
    </>
  )
}
const times = map((date: Date) =>
  format(getMinutes(date) === 30 ? 'H時m分' : 'H時')(date)
)(
  eachMinuteOfIntervalWithOptions({ step: 30 })({
    start: parseISO('1970-01-01T00:00'),
    end: parseISO('1970-01-01T23:30')
  })
)
const createDemandLabel = (
  demand: {
    amount: number
    date: string
    latest: {
      amount: number | undefined
      date: string | undefined
    }
  },
  _latestLabel: string,
  _latestFormat: string,
  maxHour: number | null | undefined,
  durations: number[][] | null | undefined,
  onClick: (event: React.MouseEvent<HTMLElement>) => void
): ReactElement => {
  const {
    amount,
    // date,
    latest: { amount: latestAmount, date: latestDate }
  } = demand

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
      <Box
        sx={{
          border: 2.5,
          p: '1em',
          mt: '4em',
          height: `${labelHeight}em`,
          width: '95%',
          borderRadius: '10px',
          borderStyle: 'dashed',
          borderColor: '#f57800',
          backgroundColor: 'white'
        }}>
        <Box
          sx={{
            height: `${labelHeight - 2}em`,
            overflowY: 'auto'
          }}>
          {!isNil(latestDate) && !isNil(latestAmount) && (
            <Typography variant="h5">
              {`・最大DM${latestAmount}kWは${format(
                endsWith(':00')(latestDate) ? 'y年M月d日H時' : 'y年M月d日H時m分'
              )(parseISO(latestDate))}に発生したDMです。`}
            </Typography>
          )}
          {!isNil(maxHour) && (
            <Typography variant="h5">{`・最もDMが発生しやすい時間帯は${
              times[maxHour] as string
            }です。${
              !isEmpty(durations)
                ? `${join('と')(
                    map(
                      (item: number[]) =>
                        `${times[head(item) as number] as string}から${
                          times[last(item) as number] as string
                        }まで`
                    )(durations)
                  )}は発生しやすい時間帯となっています。`
                : ''
            }`}</Typography>
          )}
          <Typography variant="h5">
            ・DMを抑制するためには複数の電気設備を同時に使用しないようにしてください。
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'row',
          backgroundColor: 'background.paper',
          border: 1,
          borderColor: '#f57800',
          borderRadius: '10px',
          alignItems: 'center',
          width: '90%',
          top: `-${labelHeight + 4}em`,
          position: 'relative',
          height: '5em',
          mb: '-5em'
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            pt: '0.5em',
            pb: '0.5em',
            ml: '0.5em'
          }}>
          {!isNil(latestAmount) && (
            <Typography variant="h5">
              デマンド（DM）{latestAmount.toLocaleString()}kW
            </Typography>
          )}
          {!isNil(latestAmount) && (
            <Typography variant={window.innerWidth <= 900 ? 'body1' : 'h6'}>
              【契約電力 {amount}kW（使用率
              {Math.floor((latestAmount / amount) * 100)}%）】
            </Typography>
          )}
        </Box>
        <Box>
          <IconButton size="large" onClick={onClick}>
            <HelpCenterIcon
              sx={{ color: '#f57800', boxShadow: 1 }}
              fontSize="large"
            />
          </IconButton>
        </Box>
      </Box>
      {/* {!isNil(latestAmount) && !isNil(latestDate) && (
          <Box sx={{ alignSelf: 'center' }}>
            {size(latestFormat) < 6 ? (
              <Typography variant="subtitle2" sx={{ color: 'red' }}>
                {latestLabel}DM：{latestAmount.toLocaleString()}kW（
                {format(latestFormat)(parseISO(latestDate))}）
              </Typography>
            ) : (
              <>
                <Typography variant="subtitle2" sx={{ color: 'red' }}>
                  {latestLabel}DM：{latestAmount.toLocaleString()}kW
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'red' }}>
                  （{format(latestFormat)(parseISO(latestDate))}）
                </Typography>
              </>
            )}
          </Box>
        )} */}
    </Box>
  )
}
export const ElectricityChartDaily: React.FC<
  {
    spin: string
    partner: string
    compareTo: Record<string, boolean> | undefined
    targets: Record<string, boolean> | undefined
    others: Array<RaRecord<Identifier>> | undefined
    tempature: boolean
    date: string
    classsification: string | null | undefined
    record: RaRecord
    showKinds: string[] | undefined
    amountsId?: string | undefined
    demandId?: string | undefined
  } & ScrollProps &
    ConfigProps
> = ({
  spin,
  partner,
  scrollRef,
  compareTo: compareToArg,
  targets: targetsArg,
  tempature,
  others,
  date,
  configChart,
  record,
  classsification,
  showKinds,
  amountsId,
  demandId
}) => {
  // const { permissions, isLoading: isLoadingPermissions } = usePermissions()
  const notify = useNotify()
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const targets = !isNil(targetsArg) ? keys(targetsArg) : undefined
  const compareTo = !isNil(compareToArg) ? keys(compareToArg) : undefined
  const [amountsTotal, setAmountsTotal] = React.useState<
    | {
        amounts: number
        compareAmounts: number | undefined
        compareKind: string | undefined
      }
    | undefined
  >(undefined)
  const [demand, setDemand] = React.useState<
    | {
        amount: number
        date: string
        latest: { amount: number | undefined; date: string | undefined }
      }
    | undefined
    | null
  >(undefined)
  const [targetSpin, setTargetSpin] = React.useState<string | undefined>(
    undefined
  )
  const [targetDate, setTargetDate] = React.useState<string | undefined>(
    undefined
  )
  const translate = useTranslate()
  const [analyzeData, setAnalyzeData] = React.useState<{
    compare: ElectricityDailyData | null | undefined
    target: ElectricityDailyData | null | undefined
  }>({ compare: undefined, target: undefined })
  const [action, setAction] = React.useState<string>('')
  const { data: electricityDataDaily, isLoading } =
    useGetElectricityDailyBySpin(date, spin, !isNil(demandId))
  const { data: electricityDataDailyCompareTo, isLoading: isLoadingCompareTo } =
    useGetElectricityDailyCompareToBySpin(
      date,
      spin,
      isNil(compareTo) || isNil(compareToArg)
        ? undefined
        : compact(
            map((target: string) =>
              compareToArg[target] ||
              target === ElectricityChartDailyKind.lastWeek
                ? (target as any)
                : undefined
            )(compareTo)
          )
    )
  const { data: demandData, isLoading: isLoadingDemand } =
    useGetElectricityDemand(date, spin)
  const { data: targetsData, isLoading: isLoadingTargets } =
    useGetElectricityDailyTargets(
      date,
      isNil(targets) ? undefined : targets,
      isNil(targets)
        ? undefined
        : map((target: string) => {
            const key = replace(/(.*)（.*）/, '$1')(target)
            const all = replace(/.*（(.*)）/, '$1')(target) === '全体'
            const { group } = record
            return all
              ? record[key]
              : !isNil(group)
              ? `${group as string}@${record[key] as string}`
              : undefined
          })(targets)
    )
  const { data: othersData, isLoading: isLoadingOthers } =
    useGetElectricityDailies(
      date,
      map((item: RaRecord<Identifier>) => item['spin'] as string)(others)
    )
  const othersBySpin = reduce(
    (sum: Record<string, string>, item: RaRecord<Identifier>) => ({
      ...sum,
      [item['spin'] as string]: item['name'] as string
    }),
    {}
  )(others)
  React.useEffect(() => {
    if (
      isLoading ||
      isLoadingCompareTo ||
      isLoadingDemand ||
      isLoadingTargets ||
      isLoadingOthers
    ) {
      notify('loading', {
        autoHideDuration: null
      })
    } else {
      notify('loading', {
        autoHideDuration: 0
      })
    }
    if (spin !== targetSpin || date !== targetDate) {
      if (spin !== targetSpin) setTargetSpin(spin)
      if (date !== targetDate) setTargetDate(date)
      setAmountsTotal(undefined)
      setDemand(undefined)
      setAction('')
    }
    let compareAmounts, additionalAmounts, targetsAmounts, othersAmounts
    if (
      !isNil(electricityDataDailyCompareTo) &&
      !isEmpty(electricityDataDailyCompareTo)
    ) {
      compareAmounts = compact(
        map(
          (
            item:
              | {
                  data: ElectricityDailyData | undefined
                  compareTo: ElectricityChartDailyKind
                }
              | undefined
          ) =>
            isNil(item) || isNil(compareToArg) || !compareToArg[item.compareTo]
              ? undefined
              : {
                  data:
                    !isNil(item.data) && !isNil(item.data.compareData)
                      ? map((frame: Frame) => frame.amount)(
                          item.data.compareData.amounts
                        )
                      : [],
                  rawData:
                    !isNil(item.data) && !isNil(item.data.compareData)
                      ? item.data.compareData
                      : undefined,
                  // compareTo: translate(
                  //   `resources.spots.daily.compareTo.${item.compareTo as string}`
                  // )
                  compareTo:
                    !isNil(item.data) && !isNil(item.data.compareData)
                      ? format(
                          item.compareTo === ElectricityChartDailyKind.lastWeek
                            ? 'y年M月d日'
                            : item.compareTo ===
                              ElectricityChartDailyKind.lastYearWithMonthlyAverage
                            ? 'y年M月'
                            : 'y年'
                        )(parseISO(item.data.compareData.date))
                      : ''
                }
        )(electricityDataDailyCompareTo)
      )
      // if (!isLoadingTargets && !isNil(targetsData)) {
      //   const { additionalData } = electricityDataDailyCompareTo
      //   additionalAmounts =
      //     !isNil(targetsArg) && !isNil(additionalData)
      //       ? map((frame: Framce) => frame.amount)(additionalData.amounts)
      //       : undefined
      // }
      if (
        isNil(analyzeData.compare) &&
        !isEmpty(electricityDataDailyCompareTo[0]?.data?.compareData?.amounts)
      ) {
        assign(analyzeData, {
          compare: map((frame: Frame) => frame.amount)(
            electricityDataDailyCompareTo[0]?.data?.compareData?.amounts
          )
        })
        setAnalyzeData(analyzeData)
      }
    }
    if (!isLoadingTargets && !isNil(targetsData)) {
      targetsAmounts =
        !isNil(targetsData) && !isNil(targetsArg)
          ? map(
              (item: {
                targetKey: string
                targetValue: string
                data: Record<string, ElectricityDaily>
              }) => {
                const { data: dataSet, targetKey } = item
                if (!(targetsArg[targetKey] as boolean)) {
                  return undefined
                }
                const data = dataSet[join(',')(showKinds)]
                if (isNil(data)) return undefined
                return {
                  key:
                    targetKey === 'addressState'
                      ? '都道府県'
                      : targetKey === 'addressCity'
                      ? '市区町村'
                      : targetKey,
                  data: map((frame: Frame) => frame.amount)(data.amounts)
                }
              }
            )(targetsData)
          : []
      if (isNil(analyzeData.target) && !isNil(targetsData[2])) {
        const { kind } = record
        assign(analyzeData, {
          target: map((item: { amount: number }) => item.amount)(
            targetsData[2].data[kind === '高圧' ? '高圧' : '低圧']?.amounts
          )
        })
        setAnalyzeData(analyzeData)
      }
    }
    if (!isLoadingOthers && !isNil(othersData)) {
      othersAmounts = !isNil(othersData)
        ? map((data: ElectricityDailyData | undefined) =>
            !isNil(data) && !isNil(data.data)
              ? {
                  data: map((frame: Frame) => frame.amount)(data.data.amounts),
                  name: othersBySpin[data.data.spin]
                }
              : undefined
          )(othersData)
        : []
    }
    let demand
    if (!isLoading && !isNil(electricityDataDaily)) {
      if (isNil(electricityDataDaily.data)) {
        notify('no data')
        setChart(undefined)
      }
      if (!isLoadingDemand && !isNil(demandData)) {
        const { demandData: latest } = electricityDataDaily
        if (!isNil(latest) && !isNil(demandData)) {
          const { amount, date: demandDate } = demandData
          if (!isNil(amount) && !isNil(demandDate)) {
            demand = { amount, date: demandDate, latest }
            setDemand(demand)
          }
        }
      }
      const rawData = isNil(process.env['NOT_USE_SPIN'])
        ? electricityDataDaily.data
        : find((item: ElectricityDaily) => item.spin === spin)(
            electricityDataDaily.data
          )
      if (!isNil(rawData)) {
        const { amounts: amountsRaw } = rawData
        // const times = map((frame: Frame) => frame.time)(amountsRaw)
        const amounts = map((frame: Frame) => frame.amount)(amountsRaw)
        const compareTarget = find(
          (item: { data: number[]; rawData: ElectricityDaily } | undefined) =>
            !isNil(item)
        )([
          ...(compareAmounts ?? []),
          ...(othersAmounts ?? []),
          ...(targetsAmounts ?? [])
        ])
        const compareKind = !isNil(compareTarget)
          ? find((key: string) =>
              !isNil(compareToArg) && !isNil(compareToArg[key])
                ? compareToArg[key]
                : false
            )(keys(compareToArg))
          : undefined
        setAmountsTotal({
          amounts: reduce(
            (sum: number, amount: number) => sum + amount,
            0
          )(amounts),
          compareAmounts: !isNil(compareTarget)
            ? reduce(
                (sum: number, amount: number) => sum + amount,
                0
              )(compareTarget.data)
            : undefined,
          compareKind: !isNil(compareKind)
            ? translate(`resources.spots.daily.compareTo.${compareKind}`)
            : undefined
        })
        // const maxAmounts = max([
        //   max(amounts),
        //   !isNil(lastWeekAmounts) ? max(lastWeekAmounts) : 0
        // ]) as number
        const { date, maxTemp, minTemp, temp } = rawData
        const {
          date: compareDate,
          maxTemp: compareMaxTemp,
          minTemp: compareMinTemp
        } = isNil(compareTarget)
          ? { date: undefined, maxTemp: undefined, minTemp: undefined }
          : compareTarget.rawData ?? {
              date: undefined,
              maxTemp: undefined,
              minTemp: undefined
            }
        const analyze = {
          compare: map((index: number) =>
            !isNil(amounts[index]) &&
            !isNil(analyzeData.compare) &&
            !isNil(analyzeData.compare[index])
              ? amounts[index] - analyzeData.compare[index]
              : undefined
          )(range(0)(48)),
          target: map((index: number) =>
            !isNil(amounts[index]) &&
            !isNil(analyzeData.target) &&
            !isNil(analyzeData.target[index])
              ? amounts[index] - analyzeData.target[index]
              : undefined
          )(range(0)(48))
        }
        doAnalyzeDaily(partner, date, analyze)
          .then((action) => {
            if (!isNil(action)) setAction(action)
            const chart = createDailyChart(
              date,
              amounts,
              compareAmounts,
              additionalAmounts,
              targetsAmounts,
              othersAmounts,
              { date, maxTemp, minTemp },
              isNil(compareTo)
                ? {
                    date: undefined,
                    maxTemp: undefined,
                    minTemp: undefined,
                    type: 'daily'
                  }
                : {
                    date: compareDate,
                    maxTemp: compareMaxTemp,
                    minTemp: compareMinTemp,
                    type: includes(
                      ElectricityChartDailyKind.lastWeek as string
                    )(compareTo)
                      ? 'daily'
                      : includes(
                          ElectricityChartDailyKind.lastYearWithMonthlyAverage as string
                        )(compareTo)
                      ? 'monthly'
                      : 'yearly'
                  },
              !tempature || isNil(temp)
                ? undefined
                : map((key: string) => temp[key] ?? null)(keys(temp)),
              // translate(`resources.spots.daily.compareTo.${compareTo as string}`),
              demand,
              configChart,
              classsification,
              scrollRef
            )
            setChart(chart)
          })
          .catch(log.error)
      }
    }
  }, [
    spin,
    partner,
    electricityDataDaily,
    isLoading,
    targetsArg,
    showKinds,
    isLoadingDemand,
    demandData,
    isLoadingTargets,
    targetsData,
    targetSpin,
    targetDate,
    isLoadingCompareTo,
    electricityDataDailyCompareTo,
    isLoadingOthers,
    othersData,
    tempature,
    analyzeData,
    compareToArg
  ])
  // React.useLayoutEffect(() => {
  //   scrollRef.current?.scrollIntoView()
  // })
  return (
    <>
      {!isNil(amountsId) && (
        <AmountLabel
          amountsId={amountsId}
          amountsTotal={amountsTotal}
          width="90%"
          action={action}
          date={date}
          record={record}
          partner={partner}
        />
      )}
      {!isNil(demandId) && (
        <DemandLabel
          partner={partner}
          demandId={demandId}
          demand={demand}
          latestLabel="対象日"
          latestFormat="H:mm"
        />
      )}
      {isLoading ? (
        // isLoadingPermissions ||
        // isLoadingDemand ||
        // isLoadingTargets ?
        <Loading />
      ) : !isNil(chart) ? (
        <SolarChart chart={chart} defaultHeight={window.innerHeight * 0.35} />
      ) : null}
    </>
  )
}
export const ElectricityChartMonthly: React.FC<
  {
    spin: string
    partner: string
    compareTo: Record<string, boolean> | undefined
    targets: Record<string, boolean> | undefined
    others: Array<RaRecord<Identifier>> | undefined
    tempature: boolean[]
    date: string
    inputDate: string
    classification: string | null | undefined
    showKinds: string[] | undefined
    amountsId?: string | undefined
    demandId?: string | undefined
  } & ScrollProps &
    ConfigProps
> = ({
  spin,
  partner,
  compareTo: compareToArg,
  targets: targetsArg,
  others,
  tempature,
  date,
  inputDate,
  configChart,
  record,
  showKinds,
  amountsId,
  demandId,
  scrollRef
}) => {
  // const { permissions, isLoading: isLoadingPermissions } = usePermissions()
  const notify = useNotify()
  const targets = !isNil(targetsArg) ? keys(targetsArg) : undefined
  const compareTo = !isNil(compareToArg) ? keys(compareToArg) : undefined
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_demand, setDemand] = React.useState<
    | {
        amount: number
        date: string
        latest: { amount: number | undefined; date: string | undefined }
      }
    | undefined
    | null
  >(undefined)
  const [amountsTotal, setAmountsTotal] = React.useState<
    | {
        amounts: number
        compareAmounts: number | undefined
        compareKind: string | undefined
      }
    | undefined
  >(undefined)
  const [targetSpin, setTargetSpin] = React.useState<string | undefined>(
    undefined
  )
  const [targetDate, setTargetDate] = React.useState<string | undefined>(
    undefined
  )
  const [action, setAction] = React.useState<string>('')
  const [daysAction, setDaysAction] = React.useState<Record<string, string>>({})
  const translate = useTranslate()
  const { data: electricityDataMonthly, isLoading } =
    useGetElectricityMonthlyBySpin(date, spin, !isNil(demandId))
  const {
    data: electricityDataMonthlyCompareTo,
    isLoading: isLoadingCompareTo
  } = useGetElectricityMonthlyCompareToBySpin(
    date,
    spin,
    isNil(compareTo) || isNil(compareToArg)
      ? undefined
      : compact(
          map((target: string) =>
            compareToArg[target] ? (target as any) : undefined
          )(compareTo)
        )
  )
  const { data: targetsData, isLoading: isLoadingTargets } =
    useGetElectricityMonthlyTargets(
      date,
      isNil(targets) ? undefined : targets,
      isNil(targets)
        ? undefined
        : map((target: string) => {
            const key = replace(/(.*)（.*）/, '$1')(target)
            const all = replace(/.*（(.*)）/, '$1')(target) === '全体'
            return all
              ? record[key]
              : !isNil(record.group)
              ? `${record.group as string}@${record[key] as string}`
              : undefined
          })(targets)
    )
  const { data: demandData, isLoading: isLoadingDemand } =
    useGetElectricityDemand(inputDate, spin)
  const { data: othersData, isLoading: isLoadingOthers } =
    useGetElectricityMonthlies(
      date,
      map((item: RaRecord<Identifier>) => item['spin'] as string)(others)
    )
  const othersBySpin = reduce(
    (sum: Record<string, string>, item: RaRecord<Identifier>) => ({
      ...sum,
      [item['spin'] as string]: item['name'] as string
    }),
    {}
  )(others)
  React.useEffect(() => {
    if (
      isLoading ||
      isLoadingCompareTo ||
      isLoadingDemand ||
      isLoadingTargets ||
      isLoadingOthers
    ) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (spin !== targetSpin || date !== targetDate) {
      if (spin !== targetSpin) setTargetSpin(spin)
      if (date !== targetDate) setTargetDate(date)
      setAmountsTotal(undefined)
      setDemand(undefined)
      return
    }
    let compareAmounts, targetsAmounts, othersAmounts
    if (
      !isNil(electricityDataMonthlyCompareTo) &&
      !isEmpty(electricityDataMonthlyCompareTo)
    ) {
      compareAmounts = map(
        (
          item:
            | {
                data: ElectricityMonthlyData | undefined
                compareTo: ElectricityChartMonthlyKind
              }
            | undefined
        ) =>
          isNil(item)
            ? undefined
            : {
                data:
                  !isNil(item.data) && !isNil(item.data.compareData)
                    ? map((item: { daily: number | null }) => item.daily)(
                        item.data.compareData.amounts
                      )
                    : [],
                rawData:
                  !isNil(item.data) && !isNil(item.data.compareData)
                    ? item.data.compareData
                    : undefined,
                // compareTo: translate(
                //   `resources.spots.monthly.compareTo.${
                //     item.compareTo as string
                //   }`
                // )
                compareTo:
                  !isNil(item.data) && !isNil(item.data.compareData)
                    ? format('y年M月')(
                        parseISO(item.data.compareData.date as string)
                      )
                    : ''
              }
      )(electricityDataMonthlyCompareTo)
      // if (!isLoadingTargets && !isNil(targetsData)) {
      //   const { additionalData } = electricityDataDailyCompareTo
      //   additionalAmounts =
      //     !isNil(targetsArg) && !isNil(additionalData)
      //       ? map((frame: Frame) => frame.amount)(additionalData.amounts)
      //       : undefined
      // }
    }
    if (!isLoadingTargets && !isNil(targetsData)) {
      targetsAmounts =
        !isNil(targetsData) && !isNil(targetsArg)
          ? map(
              (item: {
                targetKey: string
                targetValue: string
                data: Record<string, ElectricityMonthly>
              }) => {
                const { data: dataSet, targetKey } = item
                if (!(targetsArg[targetKey] as boolean)) {
                  return undefined
                }
                const data = dataSet[join(',')(showKinds)]
                if (isNil(data)) return undefined
                return {
                  key:
                    targetKey === 'addressState'
                      ? '都道府県'
                      : targetKey === 'addressCity'
                      ? '市区町村'
                      : targetKey,
                  data: map(
                    (item: { date: string; daily: number | null }) => item.daily
                  )(data.amounts)
                }
              }
            )(targetsData)
          : []
    }
    if (!isLoadingOthers && !isNil(othersData)) {
      othersAmounts = !isNil(othersData)
        ? map((data: ElectricityMonthlyData | undefined) =>
            !isNil(data) && !isNil(data.data)
              ? {
                  data: map((item: { daily: number | null }) => item.daily)(
                    data.data.amounts
                  ),
                  name: othersBySpin[data.data.spin]
                }
              : undefined
          )(othersData)
        : []
    }
    if (!isLoading && !isNil(electricityDataMonthly)) {
      if (isNil(electricityDataMonthly.data)) {
        notify('no data')
        setChart(undefined)
      }
      if (!isLoadingDemand && !isNil(demandData)) {
        const { demandData: latest } = electricityDataMonthly
        if (!isNil(latest) && !isNil(demandData)) {
          const { amount, date: demandDate } = demandData
          if (!isNil(amount) && !isNil(demandDate)) {
            setDemand({ amount, date: demandDate, latest })
          }
        }
      }
      const rawData = electricityDataMonthly.data
      if (!isNil(rawData)) {
        const amounts = map(
          (item: { date: string; daily: number | null }) => item.daily
        )(rawData.amounts)
        const compareTarget = find(
          (item: { data: number[]; rawData: ElectricityMonthly } | undefined) =>
            !isNil(item)
        )([
          ...(compareAmounts ?? []),
          ...(othersAmounts ?? []),
          ...(targetsAmounts ?? [])
        ])
        const amountsTotal = reduce(
          (sum: number, amount: number | null) => sum + (amount ?? 0),
          0
        )(amounts)
        const latestMonth = !isBefore(startOfMonth(Date.now()))(parseISO(date))
        const compareAmountsTotal =
          !isNil(compareTarget) && !isEmpty(compact(compareTarget.data))
            ? reduce(
                (sum: number, amount: number) => sum + amount,
                0
              )(
                slice(
                  0,
                  latestMonth ? size(compact(amounts)) : size(amounts)
                )(compareTarget.data)
              )
            : undefined
        if (
          !isLoadingCompareTo &&
          !isLoadingOthers &&
          !isLoadingTargets &&
          isNil(compareAmountsTotal)
        ) {
          notify('no compare data')
        }
        const compareKind = !isNil(compareTarget)
          ? find((key: string) =>
              !isNil(compareToArg) && !isNil(compareToArg[key])
                ? compareToArg[key]
                : false
            )(keys(compareToArg))
          : undefined
        setAmountsTotal({
          amounts: amountsTotal,
          compareAmounts: compareAmountsTotal,
          compareKind: !isNil(compareKind)
            ? translate(`resources.spots.monthly.compareTo.${compareKind}`)
            : undefined
        })
        const diff = !isNil(compareAmountsTotal)
          ? amountsTotal - (compareAmountsTotal ?? 0)
          : undefined
        const diffRatio =
          !isNil(diff) && !isNil(compareAmountsTotal)
            ? Math.floor((diff / compareAmountsTotal) * 1000) / 10
            : undefined
        const actions: any[] = []
        if (
          !isNil(diffRatio) &&
          !isNil(head(electricityDataMonthlyCompareTo))
        ) {
          const target = translate(
            `resources.spots.monthly.compareTo.${
              head(electricityDataMonthlyCompareTo)?.compareTo as string
            }`
          )
          if (diffRatio < 0) {
            actions.push({
              result: latestMonth
                ? `・最新日時点での${target}の同日時点と比較して${diffRatio}%減となっているため、現状を維持しましょう。`
                : `・${target}と比較して${diffRatio}%減となっています。`
            })
          } else {
            actions.push({
              result: latestMonth
                ? `・最新日時点での${target}の同日時点と比較して${diffRatio}%増となっているため、省エネアクションを実践してみましょう。`
                : `・${target}と比較して${diffRatio}%増となっています。`
            })
          }
        }
        actions.push(daysAction)
        setAction(JSON.stringify(actions))
        // if (isEmpty(compareDataAmounts)) {
        //   notify('no compare data')
        // }
        const { maxTemp, minTemp, temp } = rawData
        const {
          date: compareDate,
          maxTemp: compareMaxTemp,
          minTemp: compareMinTemp
        } = isNil(compareTarget)
          ? { date: undefined, maxTemp: undefined, minTemp: undefined }
          : compareTarget.rawData ?? {
              date: undefined,
              maxTemp: undefined,
              minTemp: undefined
            }
        createMonthlyChart(
          date,
          amounts,
          compareAmounts,
          targetsAmounts,
          othersAmounts,
          { date, maxTemp, minTemp },
          {
            date: compareDate,
            maxTemp: compareMaxTemp,
            minTemp: compareMinTemp,
            type: 'monthly'
          },
          // translate(`resources.spots.monthly.compareTo.${compareTo as string}`),
          !tempature[0] || isNil(temp)
            ? undefined
            : map((item: { maxTemp: number | null }) => item.maxTemp)(temp),
          !tempature[1] || isNil(temp)
            ? undefined
            : map((item: { minTemp: number | null }) => item.minTemp)(temp),
          configChart,
          scrollRef
        )
          .then((chart: ChartConfiguration) => {
            setChart(chart)
          })
          .catch(log.error)
      }
    }
  }, [
    spin,
    partner,
    electricityDataMonthly,
    isLoading,
    showKinds,
    targetsArg,
    isLoadingTargets,
    targetsData,
    targetSpin,
    targetDate,
    isLoadingDemand,
    demandData,
    isLoadingCompareTo,
    electricityDataMonthlyCompareTo,
    isLoadingOthers,
    othersData,
    tempature,
    daysAction
  ])
  React.useLayoutEffect(() => {
    if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
      scrollRef.current.scrollIntoView()
    }
  })
  return (
    <>
      {!isNil(amountsId) && (
        <AmountLabel
          amountsId={amountsId}
          amountsTotal={amountsTotal}
          width="90%"
          action={action}
        />
      )}
      <Grid container spacing={2}>
        <Grid size={7}>
          {isLoading ? (
            <Loading />
          ) : !isNil(chart) ? (
            <SolarChart
              chart={chart}
              defaultHeight={window.innerHeight * 0.35}
            />
          ) : null}
        </Grid>
        <Grid size={5}>
          <ElectricityChartMonthlyByDays
            spin={spin}
            date={format('yyyy-MM-dd')(startOfMonth(parseISO(date)))}
            configChart={configChart}
            setAction={setDaysAction}
            scrollRef={scrollRef}
          />
        </Grid>
      </Grid>
    </>
  )
}
export const ElectricityChartMonthlyByDays: React.FC<
  {
    spin: string
    date: string
    setAction: (action: Record<string, string>) => void
  } & ScrollProps &
    ConfigProps
> = ({ spin, date, configChart, setAction, scrollRef }) => {
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const { data: electricityDataMonthly, isLoading } =
    useGetElectricityMonthlyBySpin(date, spin)
  const notify = useNotify()
  React.useEffect(() => {
    if (isLoading) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (!isLoading && !isNil(electricityDataMonthly)) {
      if (isNil(electricityDataMonthly.data)) {
        notify('no data')
        setChart(undefined)
      }
      const rawData = electricityDataMonthly.data
      if (!isNil(rawData)) {
        const amounts = groupBy<{ date: string; daily: number | null }>(
          (item: { date: string }) => {
            const day = getDay(parseISO(item.date))
            return day === 0 ? 7 : day
          }
        )(rawData.amounts)
        const amountsByDays = map((day: string) => {
          const { sum, counts } = reduce(
            (
              sum: { sum: number; counts: number },
              item: { daily: number | null }
            ) => {
              if (isNil(item.daily)) {
                return sum
              } else {
                sum.sum += item.daily
                sum.counts++
                return sum
              }
            },
            { sum: 0, counts: 0 }
          )(amounts[day])
          return sum > 0 ? Math.floor(sum / counts) : 0
        })(keys(amounts))
        const maxAmount = max(amountsByDays)
        const tops: number[] = filter(
          (item: number | undefined) => !isNil(item)
        )(
          // @ts-expect-error ts(2339)
          map.convert({ cap: false })((sum: number, index: number) =>
            sum === maxAmount ? index : undefined
          )(amountsByDays)
        )
        const days = ['月', '火', '水', '木', '金', '土', '日']
        const action = `・各曜日の平均使用量では、${join('と')(
          map((day: number) => `${days[day] as string}曜日`)(tops)
        )}が最も多くなっています。`
        setAction({ result: action })
        const chart = createMonthlyChartByDays(
          date,
          amountsByDays,
          configChart,
          scrollRef
        )
        setChart(chart)
      }
    }
  }, [isLoading, electricityDataMonthly])
  return isLoading ? (
    <Loading />
  ) : !isNil(chart) ? (
    <SolarChart chart={chart} defaultHeight={window.innerHeight * 0.38} />
  ) : null
}
export const ElectricityChartYearly: React.FC<
  {
    spin: string
    partner: string
    compareTo: ElectricityChartYearlyKind
    date: string
    classification: string | null | undefined
    showKinds: string[] | undefined
    amountsId?: string | undefined
  } & ScrollProps &
    ConfigProps
> = ({
  spin,
  partner,
  compareTo: compareToArg,
  date,
  configChart,
  classification,
  showKinds,
  amountsId,
  scrollRef
}) => {
  const translate = useTranslate()
  // const { permissions, isLoading: isLoadingPermissions } = usePermissions()
  const notify = useNotify()
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const compareTo = !isNil(compareToArg) ? keys(compareToArg) : undefined
  const [amountsTotal, setAmountsTotal] = React.useState<
    | {
        amounts: number
        compareAmounts: number | undefined
        compareKind: string | undefined
      }
    | undefined
  >(undefined)
  const [targetSpin, setTargetSpin] = React.useState<string | undefined>(
    undefined
  )
  const [targetDate, setTargetDate] = React.useState<string | undefined>(
    undefined
  )
  const [analyzeData, setAnalyzeData] = React.useState<{
    compare: ElectricityYearlyData | null | undefined
  }>({ compare: undefined })
  const [action, setAction] = React.useState<string | undefined>(undefined)
  const { data: electricityDataYearly, isLoading } =
    useGetElectricityYearlyBySpin(date, spin)
  const {
    data: electricityDataYearlyCompareTo,
    isLoading: isLoadingCompareTo
  } = useGetElectricityYearlyCompareToBySpin(
    date,
    spin,
    isNil(compareTo) || isNil(compareToArg)
      ? undefined
      : compact(
          map((target: string) =>
            compareToArg[target] ||
            target === ElectricityChartYearlyKind.lastYear
              ? (target as any)
              : undefined
          )(compareTo)
        )
  )
  React.useEffect(() => {
    if (isLoading || isLoadingCompareTo) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (spin !== targetSpin || date !== targetDate) {
      if (spin !== targetSpin) setTargetSpin(spin)
      if (date !== targetDate) setTargetDate(date)
      setAmountsTotal(undefined)
      setAnalyzeData({ compare: undefined })
      setAction(undefined)
      return
    }
    let compareAmounts
    if (
      !isNil(electricityDataYearlyCompareTo) &&
      !isEmpty(electricityDataYearlyCompareTo)
    ) {
      compareAmounts = compact(
        map(
          (
            item:
              | {
                  data: ElectricityYearlyData | undefined
                  compareTo: ElectricityChartYearlyKind
                }
              | undefined
          ) =>
            isNil(item) || isNil(compareToArg) || !compareToArg[item.compareTo]
              ? undefined
              : {
                  data:
                    !isNil(item.data) && !isNil(item.data.compareData)
                      ? map((item: { daily: number | null }) => item.daily)(
                          item.data.compareData.amounts
                        )
                      : [],
                  rawData:
                    !isNil(item.data) && !isNil(item.data.compareData)
                      ? item.data.compareData
                      : undefined,
                  compareTo: translate(
                    `resources.spots.yearly.compareTo.${
                      item.compareTo as string
                    }`
                  )
                }
        )(electricityDataYearlyCompareTo)
      )
      if (
        isNil(analyzeData.compare) &&
        !isEmpty(electricityDataYearlyCompareTo[0]?.data?.compareData?.amounts)
      ) {
        assign(analyzeData, {
          compare: map((item: { daily: number | null }) => item.daily)(
            electricityDataYearlyCompareTo[0]?.data?.compareData?.amounts
          )
        })
        setAnalyzeData(analyzeData)
      }
    }
    if (!isLoading && !isNil(electricityDataYearly)) {
      if (isNil(electricityDataYearly.data)) {
        notify('no data')
        setChart(undefined)
      }
      const rawData = electricityDataYearly.data
      if (!isNil(rawData)) {
        const amounts = map(
          (item: { date: string; daily: number | null }) => item.daily
        )(rawData.amounts)
        const compareTarget = find(
          (
            item:
              | { data: number[]; rawData: ElectricityYearly | undefined }
              | undefined
          ) => !isNil(item)
        )(compareAmounts ?? [])
        const compareKind = !isNil(compareTarget)
          ? find((key: string) =>
              !isNil(compareToArg) && !isNil(compareToArg[key])
                ? compareToArg[key]
                : false
            )(keys(compareToArg))
          : undefined

        setAmountsTotal({
          amounts: reduce(
            (sum: number, amount: number | null) => sum + (amount ?? 0),
            0
          )(amounts),
          compareAmounts: !isNil(compareTarget)
            ? reduce(
                (sum: number, amount: number) => sum + amount,
                0
              )(compareTarget.data)
            : undefined,
          compareKind: !isNil(compareKind)
            ? translate(`resources.spots.yearly.compareTo.${compareKind}`)
            : undefined
        })
        const { maxTemp, minTemp } = rawData
        const {
          date: compareDate,
          maxTemp: compareMaxTemp,
          minTemp: compareMinTemp
        } = isNil(compareTarget)
          ? { date: undefined, maxTemp: undefined, minTemp: undefined }
          : compareTarget.rawData ?? {
              date: undefined,
              maxTemp: undefined,
              minTemp: undefined
            }
        if (isNil(compareTarget) || isEmpty(compareAmounts)) return
        const compareTotal = reduce(
          (sum: number, amount: number) => sum + amount,
          0
        )(compareTarget.data)
        if (compareTotal > 0) {
          const analyze = {
            compare: map((index: number) =>
              !isNil(amounts[index]) &&
              !isNil(analyzeData.compare) &&
              !isNil(analyzeData.compare[index])
                ? amounts[index] - analyzeData.compare[index]
                : undefined
            )(range(0)(12))
          }
          doAnalyzeYearly(partner, date, analyze)
            .then((action) => {
              if (!isNil(action)) setAction(action)
              const chart = createYearlyChart(
                date,
                amounts,
                compareAmounts,
                { date, maxTemp, minTemp },
                {
                  date: compareDate,
                  maxTemp: compareMaxTemp,
                  minTemp: compareMinTemp,
                  type: 'yearly'
                },
                // translate(`resources.spots.yearly.compareTo.${compareTo as string}`),
                configChart,
                classification,
                false,
                scrollRef
              )
              setChart(chart)
            })
            .catch(log.error)
        } else {
          setAction('[]')
          const chart = createYearlyChart(
            date,
            amounts,
            compareAmounts,
            { date, maxTemp, minTemp },
            {
              date: compareDate,
              maxTemp: compareMaxTemp,
              minTemp: compareMinTemp,
              type: 'yearly'
            },
            // translate(`resources.spots.yearly.compareTo.${compareTo as string}`),
            configChart,
            classification,
            false,
            scrollRef
          )
          setChart(chart)
        }
      }
    }
  }, [
    spin,
    partner,
    electricityDataYearly,
    isLoading,
    showKinds,
    targetSpin,
    targetDate,
    isLoadingCompareTo,
    electricityDataYearlyCompareTo,
    compareToArg,
    analyzeData
  ])
  // React.useLayoutEffect(() => {
  //   scrollRef.current?.scrollIntoView()
  // }, [chart])
  return (
    <>
      {!isNil(amountsId) && !isNil(action) && (
        <AmountLabel
          amountsId={amountsId}
          amountsTotal={amountsTotal}
          width="90%"
          action={action}
        />
      )}
      {isLoading ? (
        <Loading />
      ) : !isNil(chart) ? (
        <SolarChart chart={chart} />
      ) : null}
    </>
  )
}
export const ElectricityMonthlyTable: React.FC<
  {
    date: string
    spin: string
    name: string
    kind: string
  } & ScrollProps
> = ({ date, spin, name, kind, scrollRef }) => {
  const start = startOfMonth(parseISO(date))
  const end = endOfMonth(parseISO(date))
  const startDay = getDay(start) - 1 < 0 ? 6 : getDay(start) - 1
  const endDay = getDay(end) - 1 < 0 ? 6 : getDay(end) - 1
  const days = eachDayOfInterval({ start, end })
  const headNulls: Array<null | Date> =
    startDay === 0 ? [] : map((_blank: number) => null)(range(0)(startDay))
  const lastNulls: Array<null | Date> =
    endDay === 6 ? [] : map((_blank: number) => null)(range(0)(6 - endDay))
  const all = [...headNulls, ...days, ...lastNulls]
  const { data: electricityDataMonthly, isLoading } =
    useGetElectricityMonthlyBySpin(date, spin)
  const {
    data: electricityDataMonthlyCompareTo,
    isLoading: isLoadingCompareTo
  } = useGetElectricityMonthlyCompareToBySpin(date, spin, [
    ElectricityChartMonthlyKind.lastYear
  ])
  const notify = useNotify()
  const [amounts, setAmounts] = React.useState<
    Array<number | null> | undefined
  >(undefined)
  const [demands, setDemands] = React.useState<
    Array<number | null> | undefined
  >(undefined)
  const [compareAmounts, setCompareAmounts] = React.useState<
    Array<number | null> | undefined
  >(undefined)
  const [compareDemands, setCompareDemands] = React.useState<
    Array<number | null> | undefined
  >(undefined)
  const [compareMaxTemps, setCompareMaxTemps] = React.useState<
    Array<number | null> | undefined
  >(undefined)
  const [maxTemps, setMaxTemps] = React.useState<
    Array<number | null> | undefined
  >(undefined)
  const translate = useTranslate()
  React.useEffect(() => {
    if (isLoading || isLoadingCompareTo) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (
      !isNil(electricityDataMonthlyCompareTo) &&
      !isEmpty(electricityDataMonthlyCompareTo) &&
      !isNil(electricityDataMonthlyCompareTo[0]) &&
      !isNil(electricityDataMonthlyCompareTo[0].data) &&
      !isNil(electricityDataMonthlyCompareTo[0].data.compareData)
    ) {
      const compareAmounts = map((item: { daily: number | null }) =>
        !isNil(item.daily) ? Math.round(item.daily * 10) / 10 : null
      )(electricityDataMonthlyCompareTo[0].data.compareData.amounts)
      setCompareAmounts(compareAmounts)
      const compareMaxTemps = map(
        (item: { maxTemp: number | null }) => item.maxTemp
      )(electricityDataMonthlyCompareTo[0].data.compareData.temp)
      setCompareMaxTemps(compareMaxTemps)
      if (includes('高圧')(kind)) {
        const demands = map(
          (item: { date: string; halfHourly: number[] }) =>
            (max(item.halfHourly) ?? 0) * 2
        )(electricityDataMonthlyCompareTo[0].data.compareData.amounts)
        setCompareDemands(demands)
      }
    }
    if (!isLoading && !isNil(electricityDataMonthly)) {
      if (isNil(electricityDataMonthly.data)) {
        notify('no data')
      }
      const rawData = electricityDataMonthly.data
      if (!isNil(rawData)) {
        const amounts = map((item: { date: string; daily: number | null }) =>
          !isNil(item.daily) ? Math.round(item.daily * 10) / 10 : null
        )(rawData.amounts)
        setAmounts(amounts)
        setMaxTemps(
          map((item: { maxTemp: number | null }) => item.maxTemp)(rawData.temp)
        )
        if (includes('高圧')(kind)) {
          const demands = map(
            (item: { date: string; halfHourly: number[] }) => {
              const maxAmount = max(item.halfHourly)
              return isNil(maxAmount) ? null : maxAmount * 2
            }
          )(rawData.amounts)
          setDemands(demands)
        }
      }
    }
    if (!includes('高圧')(kind)) {
      setDemands(undefined)
      setCompareDemands(undefined)
    }
  }, [isLoading, isLoadingCompareTo, spin, date])
  const handleDownload = (): void => {
    if (isNil(amounts) || isNil(compareAmounts) || isNil(maxTemps)) return
    const data = map((day: null | Date) => {
      const index = !isNil(day) ? getDate(day) - 1 : null
      const demand = !isNil(demands) && !isNil(index) ? demands[index] : null
      const compareDemand =
        !isNil(compareDemands) && !isNil(index) ? compareDemands[index] : null
      const amount = !isNil(index) ? amounts[index] : null
      const compareAmount = !isNil(index) ? compareAmounts[index] : null
      const maxTemp =
        !isNil(amount) && !isNil(maxTemps) && !isNil(index)
          ? maxTemps[index]
          : null
      const compareMaxTemp =
        !isNil(amount) && !isNil(compareMaxTemps) && !isNil(index)
          ? compareMaxTemps[index]
          : null
      const amountDiff =
        !isNil(compareAmount) && !isNil(amount)
          ? Math.round((compareAmount - amount) * 10) / 10
          : null
      const demandDiff =
        !isNil(compareDemand) && !isNil(demand)
          ? Math.round((compareDemand - demand) * 10) / 10
          : null
      const maxTempDiff =
        !isNil(compareMaxTemp) && !isNil(maxTemp)
          ? Math.floor((maxTemp - compareMaxTemp) * 10) / 10
          : null
      const value = !isNil(day) ? getDate(day) : ''
      const demandValue = !isNil(demand) ? `${demand}kW` : ''
      const compareDemandValue = !isNil(demandDiff)
        ? `(${demandDiff >= 0 ? '+' : '▲'}${
            demandDiff < 0 ? -demandDiff : demandDiff
          }kW)`
        : ''

      const amountValue = !isNil(amount) ? `${amount}kWh` : ''
      const compareAmountValue = !isNil(amountDiff)
        ? `(${amountDiff >= 0 ? '+' : '▲'}${
            amountDiff < 0 ? -amountDiff : amountDiff
          }kWh)`
        : ''
      const maxTempValue = !isNil(maxTemp) ? `${maxTemp}℃` : ''
      const compareMaxTempValue = !isNil(maxTempDiff)
        ? `(${maxTempDiff >= 0 ? '+' : '▲'}${
            maxTempDiff < 0 ? -maxTempDiff : maxTempDiff
          }℃)`
        : ''
      return includes('高圧')(kind)
        ? {
            value,
            demandValue,
            compareDemandValue,
            amountValue,
            compareAmountValue,
            maxTempValue,
            compareMaxTempValue
          }
        : {
            value,
            amountValue,
            compareAmountValue,
            maxTempValue,
            compareMaxTempValue
          }
    })(all)
    const headers = ['', '月', '火', '水', '木', '金', '土', '日']
    const counts = size(data)
    let count = 0
    const results: any[] = []
    while (count < counts) {
      results.push(
        map((keys: string[]) => {
          return reduce(
            (
              sum: Record<string, number | string>,
              item: Record<string, number | string>
            ) => ({ ...sum, ...item }),
            {}
          )(
            flatten(
              // @ts-expect-error ts(2339)
              map.convert({ cap: false })((item: any, index: number) =>
                // @ts-expect-error ts(2339)
                map.convert({ cap: false })((key: string, keyIndex: number) => {
                  if (index === 0) {
                    return {
                      [`${index}-${keyIndex}`]: !isEmpty(key) ? item[key] : ''
                    }
                  } else {
                    return {
                      [`${index}-${keyIndex}`]: !isEmpty(key) ? item[key] : ''
                    }
                  }
                })(keys)
              )(
                concat([
                  includes('高圧')(kind)
                    ? {
                        value: '',
                        demandValue: 'デマンド',
                        compareDemandValue: '（前年同曜日比）',
                        amountValue: '電力量',
                        compareAmountValue: '（前年同曜日比）',
                        maxTempValue: '最高気温',
                        compareMaxTempValue: '（前年同曜日比）'
                      }
                    : {
                        value: '',
                        amountValue: '電力量',
                        compareAmountValue: '（前年同曜日比）',
                        maxTempValue: '最高気温',
                        compareMaxTempValue: '（前年同曜日比）'
                      }
                ])(slice(count, count + 7)(data) as any[])
              )
            )
          )
        })(
          includes('高圧')(kind)
            ? [
                ['value', ''],
                ['amountValue', 'demandValue'],
                ['compareAmountValue', 'compareDemandValue'],
                ['', 'maxTempValue'],
                ['', 'compareMaxTempValue']
              ]
            : [
                ['value', ''],
                ['amountValue', ''],
                ['compareAmountValue', ''],
                ['', 'maxTempValue'],
                ['', 'compareMaxTempValue']
              ]
        )
      )
      count += 7
    }
    const items = 5
    const cellStyles = flatten(
      map((row: number) => {
        return {
          column: 1,
          counts: 16,
          row,
          styles: map((column: number) => {
            let diff: number | undefined
            const rowIndex = Math.floor((row - 2) / items)
            const columnIndex = Math.floor(column / 2)
            if (
              includes('高圧')(kind) &&
              (row - 2) % items === 2 &&
              columnIndex > 0 &&
              column % 2 === 1
            ) {
              const index = results[rowIndex][0][`${columnIndex}-0`] - 1
              if (
                !isNil(demands) &&
                !isNil(demands[index]) &&
                !isNil(compareDemands) &&
                !isNil(compareDemands[index])
              ) {
                diff = compareDemands[index] - demands[index]
              }
            }
            if (
              (row - 2) % items === 2 &&
              columnIndex > 0 &&
              column % 2 === 0
            ) {
              const index = results[rowIndex][0][`${columnIndex}-0`] - 1
              if (
                !isNil(amounts) &&
                !isNil(amounts[index]) &&
                !isNil(compareAmounts) &&
                !isNil(compareAmounts[index])
              ) {
                diff = compareAmounts[index] - amounts[index]
              }
            }
            if (
              (row - 2) % items === 4 &&
              columnIndex > 0 &&
              column % 2 === 1
            ) {
              const index = results[rowIndex][0][`${columnIndex}-0`] - 1
              if (
                !isNil(maxTemps) &&
                !isNil(maxTemps[index]) &&
                !isNil(compareMaxTemps) &&
                !isNil(compareMaxTemps[index])
              ) {
                diff = maxTemps[index] - compareMaxTemps[index]
              }
            }
            return (row - 2) % items === 0 && columnIndex > 0
              ? {
                  fill: { type: 'solid', color: 'FEF7E3' },
                  horizontalAlignment: 'center'
                }
              : !isNil(diff)
              ? {
                  horizontalAlignment: 'center',
                  fontColor: diff >= 0 ? 'FF0000' : '0000FF',
                  border:
                    column % 2 === 0
                      ? {
                          left: {
                            style: 'thin',
                            color: '000000'
                          },
                          right: {
                            style: 'thin',
                            color: '000000'
                          }
                        }
                      : {
                          left: {
                            style: 'thin',
                            color: '000000'
                          },
                          right: {
                            style: 'thin',
                            color: '000000'
                          },
                          bottom: {
                            style: 'thin',
                            color: '000000'
                          }
                        }
                }
              : {
                  horizontalAlignment: 'center',
                  border:
                    column % 2 === 0 &&
                    ((row - 2) % items === 2 || (row - 2) % items === 3)
                      ? {
                          left: {
                            style: 'thin',
                            color: '000000'
                          },
                          right: {
                            style: 'thin',
                            color: '000000'
                          }
                        }
                      : (row - 2) % items === 1 || (row - 2) % items === 3
                      ? {
                          left: {
                            style: 'thin',
                            color: '000000'
                          },
                          right: {
                            style: 'thin',
                            color: '000000'
                          },
                          top: {
                            style: 'thin',
                            color: '000000'
                          }
                        }
                      : {
                          left: {
                            style: 'thin',
                            color: '000000'
                          },
                          right: {
                            style: 'thin',
                            color: '000000'
                          },
                          bottom: {
                            style: 'thin',
                            color: '000000'
                          }
                        }
                }
          })(range(0)(16))
        }
      })(range(2)(2 + size(results) * items))
    )
    json2xlsx(flatten(results), undefined, {
      // startRow: items + 1,
      headerStyle: {
        border: {
          left: {
            style: 'thin',
            color: '000000'
          },
          right: {
            style: 'thin',
            color: '000000'
          },
          top: {
            style: 'thin',
            color: '000000'
          },
          bottom: {
            style: 'thin',
            color: '000000'
          }
        },
        horizontalAlignment: 'center'
      },
      cellStyles,
      headers: reduce((sum: Record<string, string>, index: number) => {
        const items = {
          [`${index}-0`]: headers[index] as string,
          [`${index}-1`]: ''
        }
        return { ...sum, ...items }
      }, {})(range(0)(8)),
      type: 'workbook'
    })
      .then((workbook: Workbook | undefined) => {
        if (!isNil(workbook)) {
          const worksheet = workbook.activeSheet()
          for (let i = 1; i < 17; i += 2) {
            worksheet.range(1, i, 1, i + 1).merged(true)
          }
          for (let row = 0; row < size(results); row++) {
            for (let i = 1; i < 17; i += 2) {
              const rowBase = 2 + row * items
              worksheet.range(rowBase, i, rowBase, i + 1).merged(true)
            }
          }
          workbook.outputAsync().then((data: Uint8Array | undefined) => {
            if (!isNil(data)) {
              const blob = new Blob([Buffer.from(data)])
              const url = URL.createObjectURL(blob)
              const download = document.createElement('a')
              download.href = url
              download.download = `${format('yyyyMM')(start)}${
                name as string
              }_${format('yyyyMMdd')(Date.now())}.xlsx`
              download.click()
              window.URL.revokeObjectURL(url)
            }
          })
        }
      })
      .catch(log.error)
  }
  // })
  // .catch(log.error)
  // }
  React.useLayoutEffect(() => {
    if (!isNil(scrollRef) && !isNil(scrollRef.current)) {
      scrollRef.current.scrollIntoView()
    }
  })
  if (isLoading || isLoadingCompareTo) return <Loading />
  if (isNil(amounts) || isNil(compareAmounts) || isNil(maxTemps)) return null
  const label = (
    <Box sx={{ display: 'flex' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          border: 1,
          flexGrow: 1
        }}>
        <Typography>電力量</Typography>
        <Typography sx={{ fontSize: 12 }}>(前年同曜日比)</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {!isNil(demands) && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              border: 1
            }}>
            <Typography>デマンド</Typography>
            <Typography sx={{ fontSize: 12 }}>(前年同曜日比)</Typography>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            border: 1
          }}>
          <Typography>最高気温</Typography>
          <Typography sx={{ fontSize: 12 }}>(前年同曜日比)</Typography>
        </Box>
      </Box>
    </Box>
  )
  const tableLabel = (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          border: 1
        }}>
        {'　'}
      </Box>
      {label}
    </>
  )
  return (
    <Box
      sx={{
        // display: 'flex',
        // justifyContent: 'center',
        bgcolor: 'white',
        p: '2em',
        mt: '2em'
      }}>
      <Grid container justifyContent="space-between">
        <Grid size={3}>
          <Button variant="contained" onClick={handleDownload}>
            {translate('resources.spots.buttons.download')}
          </Button>
        </Grid>
        {/* <Grid size={1.714}>{label}</Grid> */}
      </Grid>
      <Box sx={{ mt: '2em' }}>
        <Grid container gap={0}>
          <Grid size={2}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                border: 1
              }}>
              {'　'}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {map((_idex: number) => tableLabel)(
                range(0)(
                  getWeeksInMonthWithOptions({ weekStartsOn: 1 })(
                    parseISO(date)
                  )
                )
              )}
            </Box>
          </Grid>
          <Grid size={10}>
            <Grid container>
              {map((title: string) => (
                <Grid size={1.714} key={title}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      border: 1
                    }}>
                    {title}
                  </Box>
                </Grid>
              ))(['月', '火', '水', '木', '金', '土', '日'])}
              {
                // @ts-expect-error ts(2339)
                map.convert({ cap: false })(
                  (day: null | Date, count: number) => {
                    const index = !isNil(day) ? getDate(day) - 1 : null
                    const demand =
                      !isNil(demands) && !isNil(index) ? demands[index] : null
                    const compareDemand =
                      !isNil(compareDemands) && !isNil(index)
                        ? compareDemands[index]
                        : null
                    const amount = !isNil(index) ? amounts[index] : null
                    const compareAmount = !isNil(index)
                      ? compareAmounts[index]
                      : null
                    const maxTemp =
                      !isNil(amount) && !isNil(maxTemps) && !isNil(index)
                        ? maxTemps[index]
                        : null
                    const compareMaxTemp =
                      !isNil(amount) && !isNil(compareMaxTemps) && !isNil(index)
                        ? compareMaxTemps[index]
                        : null
                    const amountDiff =
                      !isNil(compareAmount) && !isNil(amount)
                        ? Math.round((compareAmount - amount) * 10) / 10
                        : null
                    const demandDiff =
                      !isNil(compareDemand) && !isNil(demand)
                        ? Math.round((compareDemand - demand) * 10) / 10
                        : null
                    const maxTempDiff =
                      !isNil(compareMaxTemp) && !isNil(maxTemp)
                        ? Math.floor((maxTemp - compareMaxTemp) * 10) / 10
                        : null
                    return (
                      <Grid size={1.714} key={count}>
                        <>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              border: 1,
                              bgcolor: '#FFF7E1'
                            }}>
                            {!isNil(day) ? (
                              getDate(day)
                            ) : (
                              <Typography>&nbsp;</Typography>
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'space-between'
                            }}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                border: 1,
                                flexGrow: 1
                              }}>
                              {amount ?? <Typography>&nbsp;</Typography>}
                              {!isNil(amount) ? 'kWh' : ''}
                              {!isNil(amountDiff) ? (
                                <Typography
                                  sx={{ fontSize: 12 }}
                                  color={amountDiff >= 0 ? 'red' : 'blue'}>
                                  ({amountDiff >= 0 ? '+' : '▲'}
                                  {amountDiff < 0 ? -amountDiff : amountDiff}
                                  kWh)
                                </Typography>
                              ) : (
                                <Typography sx={{ fontSize: 12 }}>
                                  &nbsp; &nbsp; &nbsp;
                                </Typography>
                              )}
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '40%'
                              }}>
                              {!isNil(demands) && !isNil(compareDemands) && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    border: 1
                                  }}>
                                  {demand ?? <Typography>&nbsp;</Typography>}
                                  {!isNil(demand) ? 'kW' : ''}
                                  {!isNil(demandDiff) ? (
                                    <Typography
                                      sx={{ fontSize: 12 }}
                                      color={demandDiff >= 0 ? 'red' : 'blue'}>
                                      ({demandDiff >= 0 ? '+' : '▲'}
                                      {demandDiff < 0
                                        ? -demandDiff
                                        : demandDiff}
                                      kW)
                                    </Typography>
                                  ) : (
                                    <Typography sx={{ fontSize: 12 }}>
                                      &nbsp;
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  border: 1
                                }}>
                                {maxTemp ?? <Typography>&nbsp;</Typography>}
                                {!isNil(maxTemp) ? '℃' : ''}
                                {!isNil(maxTempDiff) ? (
                                  <Typography
                                    sx={{ fontSize: 12 }}
                                    color={maxTempDiff >= 0 ? 'red' : 'blue'}>
                                    ({maxTempDiff >= 0 ? '+' : '▲'}
                                    {maxTempDiff < 0
                                      ? -maxTempDiff
                                      : maxTempDiff}
                                    ℃)
                                  </Typography>
                                ) : (
                                  <Typography sx={{ fontSize: 12 }}>
                                    &nbsp;
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </>
                      </Grid>
                    )
                  }
                )(all)
              }
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
export const DemandChartYearly: React.FC<
  {
    spin: string
    partner: string
    date: string
    demandId?: string | undefined
  } & ScrollProps &
    ConfigProps
> = ({ spin, partner, date, demandId, configChart, scrollRef }) => {
  const notify = useNotify()
  const navigate = useNavigate()
  const { id: target } = useRecordContext() ?? { id: undefined }
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const [demand, setDemand] = React.useState<
    | {
        amount: number
        date: string
        latest: { date: string; amount: number | undefined }
      }
    | undefined
    | null
  >(undefined)

  const [targetSpin, setTargetSpin] = React.useState<string | undefined>(
    undefined
  )
  const [targetDate, setTargetDate] = React.useState<string | undefined>(
    undefined
  )
  const { data: demandData, isLoading } = useGetElectricityDemand(date, spin)
  React.useEffect(() => {
    if (isLoading) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (spin !== targetSpin || date !== targetDate) {
      if (spin !== targetSpin) setTargetSpin(spin)
      if (date !== targetDate) setTargetDate(date)
      setDemand(undefined)
    }
    if (!isNil(demandData)) {
      const { amount, date: demandDate, amounts } = demandData
      const { date, maxAmount } = last(amounts) ?? {
        date: '',
        maxAmount: { amount: 0 }
      }
      setDemand({
        amount: amount ?? 0,
        date: demandDate ?? '',
        latest: { date, amount: maxAmount.amount }
      })
      const chart = createDemandYearlyChart(
        date,
        map((item: { maxAmount: { amount: number } }) => item.maxAmount.amount)(
          amounts
        ),
        amount,
        configChart,
        scrollRef
      )
      setChart(chart)
    }
  }, [isLoading, demandData, targetSpin, targetDate, spin, date])
  // React.useEffect(() => {
  //   scrollRef.current?.scrollIntoView()
  // }, [chart])
  const onClick = (index: number): void => {
    if (isNil(demandData)) return
    const { amounts } = demandData
    if (isNil(amounts)) return
    const { date } = amounts[index - 1] ?? { date: undefined }
    if (isNil(date)) return
    const path = `demand/${target as string}/${date as string}/monthly`
    navigate({
      pathname: path
    })
  }
  return (
    <>
      {!isNil(demandId) && (
        <DemandLabel
          partner={partner}
          demandId={demandId}
          demand={demand}
          latestLabel="対象日"
          latestFormat="H:mm"
        />
      )}
      {isLoading ? (
        <Loading />
      ) : !isNil(chart) ? (
        <SolarChart chart={chart} onClickItem={onClick} />
      ) : null}
    </>
  )
}
export const DemandChartMonthly: React.FC<
  {
    spin: string
    partner: string
    date: string
    demandId?: string | undefined
  } & ScrollProps &
    ConfigProps
> = ({ spin, partner, date, demandId, configChart, scrollRef }) => {
  const notify = useNotify()
  const navigate = useNavigate()
  const { id: target } = useRecordContext() ?? { id: undefined }
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const [demand, setDemand] = React.useState<
    | {
        amount: number
        date: string
        latest: { date: string; amount: number | undefined }
      }
    | undefined
    | null
  >(undefined)

  const [targetSpin, setTargetSpin] = React.useState<string | undefined>(
    undefined
  )
  const [targetDate, setTargetDate] = React.useState<string | undefined>(
    undefined
  )
  const { data: demandData, isLoading } = useGetElectricityDemandMonthly(
    date,
    spin
  )
  const { data: demandYearlydData, isLoading: isLoadingYearly } =
    useGetElectricityDemand(date, spin)
  const times = map((date: Date) => format('HH:mm')(date))(
    eachMinuteOfIntervalWithOptions({ step: 30 })({
      start: parseISO('1970-01-01T00:00'),
      end: parseISO('1970-01-01T23:30')
    })
  )
  React.useEffect(() => {
    if (isLoading || isLoadingYearly) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (spin !== targetSpin || date !== targetDate) {
      if (spin !== targetSpin) setTargetSpin(spin)
      if (date !== targetDate) setTargetDate(date)
      setDemand(undefined)
    }
    if (!isNil(demandData) && !isNil(demandYearlydData)) {
      const { amount: yearlyDemand } = demandYearlydData
      const { date: demandDate, demands } = demandData
      const amounts = map(
        (item: { date: string; amount: number; demands: number[] }) => {
          const { date, amount, demands } = item
          const demandTime = head(demands)
          const time = !isNil(demandTime) ? (times[demandTime] as string) : ''
          return { date, amount, time }
        }
      )(demands)
      const maxAmount = maxBy<{ date: string; amount: number; time: string }>(
        (item: { date: string; amount: number; time: string }) => item.amount
      )(amounts)
      const { date, amount, time } = maxAmount ?? {
        date: '',
        amount: 0,
        time: ''
      }
      setDemand({
        date:
          !isEmpty(demandDate) && !isEmpty(time)
            ? `${demandDate as string}T${time}`
            : '',
        amount: yearlyDemand ?? 0,
        latest: { date, amount }
      })
      const chart = createDemandMonthlyChart(
        demandDate,
        map((item: { amount: number }) => item.amount)(amounts),
        amount,
        configChart,
        scrollRef
      )
      setChart(chart)
    }
  }, [
    isLoading,
    isLoadingYearly,
    demandData,
    demandYearlydData,
    targetSpin,
    targetDate,
    spin,
    date
  ])
  // React.useEffect(() => {
  //   scrollRef.current?.scrollIntoView()
  // }, [chart])
  const onClick = (index: number): void => {
    if (isNil(demandData)) return
    const { date: demandDate } = demandData
    const date = format('yyyy-MM-dd')(addDays(index - 1)(parseISO(demandDate)))
    if (isNil(date)) return
    const path = `demand/${target as string}/${date}/daily`
    const { pathname } = window.location
    if (includes('spot/demand')(pathname)) {
      navigate({
        pathname: `${join('/')(slice(0, 4)(split('/')(pathname)))}/${path}`
      })
    } else {
      navigate({
        pathname: path
      })
    }
  }
  return (
    <>
      {!isNil(demandId) && (
        <DemandLabel
          partner={partner}
          demandId={demandId}
          demand={demand}
          latestLabel="対象日"
          latestFormat="H:mm"
        />
      )}
      {isLoading ? (
        <Loading />
      ) : !isNil(chart) ? (
        <SolarChart chart={chart} onClickItem={onClick} />
      ) : null}
    </>
  )
}
export const DemandChartDaily: React.FC<
  {
    spin: string
    partner: string
    date: string
    demandId?: string | undefined
  } & ScrollProps &
    ConfigProps
> = ({ spin, partner, date, demandId, configChart, scrollRef }) => {
  const notify = useNotify()
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const [demand, setDemand] = React.useState<
    | {
        amount: number
        date: string
        latest: { date: string; amount: number | undefined }
      }
    | undefined
    | null
  >(undefined)

  const [targetSpin, setTargetSpin] = React.useState<string | undefined>(
    undefined
  )
  const [targetDate, setTargetDate] = React.useState<string | undefined>(
    undefined
  )
  const { data: demandData, isLoading } = useGetElectricityDemandDaily(
    date,
    spin
  )
  const { data: demandYearlydData, isLoading: isLoadingYearly } =
    useGetElectricityDemand(date, spin)
  const times = map((date: Date) => format('HH:mm')(date))(
    eachMinuteOfIntervalWithOptions({ step: 30 })({
      start: parseISO('1970-01-01T00:00'),
      end: parseISO('1970-01-01T23:30')
    })
  )
  React.useEffect(() => {
    if (isLoading || isLoadingYearly) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (spin !== targetSpin || date !== targetDate) {
      if (spin !== targetSpin) setTargetSpin(spin)
      if (date !== targetDate) setTargetDate(date)
      setDemand(undefined)
    }
    if (
      !isNil(demandData) &&
      !isEmpty(demandData.demands) &&
      !isNil(demandData.demands[0]) &&
      !isNil(demandYearlydData)
    ) {
      const { amount: yearlyDemand } = demandYearlydData
      const {
        date: demandDate,
        demands: [{ demands, amounts }]
      } = demandData
      const demandTime = head(demands)
      const amount =
        !isNil(demandTime) && !isNil(amounts[demandTime])
          ? amounts[demandTime]
          : 0
      const time = !isNil(demandTime) ? times[demandTime] : ''
      setDemand({
        date:
          !isEmpty(demandDate) && !isEmpty(time)
            ? `${demandDate as string}T${time as string}`
            : '',
        amount: yearlyDemand ?? 0,
        latest: { date, amount }
      })
      const chart = createDemandDailyChart(
        demandDate,
        map((amount: number) => amount)(amounts),
        amount,
        configChart,
        scrollRef
      )
      setChart(chart)
    }
  }, [
    isLoading,
    isLoadingYearly,
    demandYearlydData,
    demandData,
    targetSpin,
    targetDate,
    spin,
    date
  ])
  // React.useEffect(() => {
  //   scrollRef.current?.scrollIntoView()
  // }, [chart])
  return (
    <>
      {!isNil(demandId) && (
        <DemandLabel
          partner={partner}
          demandId={demandId}
          demand={demand}
          latestLabel="対象日"
          latestFormat="H:mm"
        />
      )}
      {isLoading ? (
        <Loading />
      ) : !isNil(chart) ? (
        <SolarChart chart={chart} />
      ) : null}
    </>
  )
}
export const DemandChartTrend: React.FC<
  {
    spin: string
    partner: string
    date: string
    demandId?: string | undefined
  } & ScrollProps &
    ConfigProps
> = ({ spin, partner, date, demandId, configChart, scrollRef }) => {
  const notify = useNotify()
  const translate = useTranslate()
  const [chart, setChart] = React.useState<ChartConfiguration | undefined>(
    undefined
  )
  const [demand, setDemand] = React.useState<
    | {
        amount: number
        date: string
        latest: { date: string; amount: number | undefined }
      }
    | undefined
    | null
  >(undefined)

  const [targetSpin, setTargetSpin] = React.useState<string | undefined>(
    undefined
  )
  const [targetDate, setTargetDate] = React.useState<string | undefined>(
    undefined
  )
  const [maxHour, setMaxHour] = React.useState<number>(-1)
  const [durations, setDurations] = React.useState<number[][]>([])
  const { data: demandData, isLoading } = useGetElectricityDemand(date, spin)
  const { data: demandDataMonthly, isLoading: isLoadingMonghly } =
    useGetElectricityDemandMonthly(date, spin)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = (): void => {
    setAnchorEl(null)
  }
  React.useEffect(() => {
    if (isLoading || isLoadingMonghly) {
      notify('loading', { autoHideDuration: null })
    } else {
      notify('loading', { autoHideDuration: 0 })
    }
    if (spin !== targetSpin || date !== targetDate) {
      if (spin !== targetSpin) setTargetSpin(spin)
      if (date !== targetDate) setTargetDate(date)
      setDemand(undefined)
    }
    if (!isNil(demandData)) {
      const { amount, date: demandDate, amounts } = demandData
      const { date, maxAmount } = last(amounts) ?? {
        date: '',
        maxAmount: { amount: 0 }
      }
      setDemand({
        amount: amount ?? 0,
        date: demandDate ?? '',
        latest: { date, amount: maxAmount.amount }
      })
    }
    if (!isNil(demandDataMonthly)) {
      const { demands } = demandDataMonthly
      const values = reduce((sum: number[], item: { demands: number[] }) => {
        const { demands } = item
        map((index: number) => {
          sum[index] = (sum[index] ?? 0) + 1
        })(demands)
        return sum
      }, new Array(48))(demands)
      const maxValue = max(values)
      const maxHour = findIndex((item: number) => item === maxValue)(values)
      setMaxHour(maxHour)
      const average = Math.floor(
        reduce(
          (sum: number, value: number) => sum + value,
          0
        )(compact(values)) / size(compact(values))
      )
      // @ts-expect-error ts(2339)
      const valuesWithIndex = map.convert({ cap: false })(
        (value: number, index: number) => ({
          value,
          index
        })
      )(values)
      const durations = map((item: { index: number }) => item.index)(
        filter(
          (item: { value: number; index: number }) =>
            item.value >= average &&
            (item.index === size(valuesWithIndex) - 1 ||
              valuesWithIndex[item.index + 1].value >= average)
        )(valuesWithIndex)
      )
      const maxDurations: number[][] = []
      let partial: number[] = []
      // @ts-expect-error ts(2339)
      map.convert({ cap: false })((value: number, index: number) => {
        if (index === 0 || durations[index - 1] === value - 1) {
          partial.push(value)
        } else {
          if (size(partial) > size(last(maxDurations))) {
            maxDurations.push(partial)
          }
          partial = [value]
        }
      })(durations)
      if (size(partial) > size(last(maxDurations))) {
        maxDurations.push(partial)
      }
      setDurations(maxDurations)
      const chart = createDemandTrendChart(values, configChart, scrollRef)
      setChart(chart)
    }
  }, [
    isLoading,
    isLoadingMonghly,
    demandData,
    demandDataMonthly,
    targetSpin,
    targetDate,
    spin,
    date
  ])
  // React.useLayoutEffect(() => {
  //   scrollRef.current?.scrollIntoView()
  // })
  return (
    <>
      {!isNil(demandId) && (
        <DemandLabel
          partner={partner}
          demandId={demandId}
          demand={demand}
          latestLabel="対象日"
          latestFormat="H:mm"
          maxHour={maxHour}
          durations={durations}
        />
      )}
      {isLoading ? (
        <Loading />
      ) : !isNil(chart) ? (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: '-4em' }}>
            <IconButton size="large" onClick={handleClick}>
              <HelpCenterIcon
                sx={{ color: '#f57800', boxShadow: 1 }}
                fontSize="large"
              />
            </IconButton>
          </Box>
          <SolarChart chart={chart} />
          <Popover
            open={!isNil(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}>
            <Box sx={{ border: 1, p: 1, bgcolor: 'background.paper' }}>
              {
                // @ts-expect-error ts(2339)
                map.convert({ cap: false })((line: string, index: number) => (
                  <Typography variant="inherit" key={index}>
                    {line}
                    <br />
                  </Typography>
                ))(
                  split('\n')(translate('resources.spots.demandTabs.trendHint'))
                )
              }
            </Box>
          </Popover>
        </>
      ) : null}
    </>
  )
}
